import { useState, useCallback } from 'react'
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Button,
  IconButton,
  Slider,
  Box,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Delete from '@mui/icons-material/Delete'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { getNumberStringForInput } from 'src/mentor/helpers/utilityFunctions'
import {
  DEFAULT_RX_PRINT_COLOR_STYLES,
  RX_PRINT_COLOR_STYLE_FIELDS,
} from 'src/mentor/components/PrescriptionMakingPage/printComponents/drugAdvicePrintColorStyles'
import { isDrugAdviceEmrSection } from '../utils/emrSectionIds'
import { HEADING_CASE_OPTIONS } from '../utils/bodyStyleOverrideUtils'

function getColorInputValue(value, fallback) {
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(value || '') ? value : fallback
}

/**
 * Style editor component for PrintedReportSectionStyles
 * Takes overrideData and setOverrideData as props
 * Keeps UI logic-free, all state management via props
 */
export default function PrescriptionTemplateBodySectionsStylesOverrideHandler({
  emrSectionId,
  overrideData = {},
  setOverrideData,
  emrSectionName: _emrSectionName,
  disabled = false,
}) {
  const [newStyleKey, setNewStyleKey] = useState('')
  const [newStyleValue, setNewStyleValue] = useState('')



  // Helper to create event-like object for setOverrideData
  const createChangeEvent = useCallback(
    (id, value) => ({
      target: { id, value },
    }),
    [],
  )

  // Handle basic field changes
  const handleFieldChange = useCallback(
    (e) => {
      setOverrideData(createChangeEvent(e.target.id, e.target.value))
    },
    [createChangeEvent, setOverrideData],
  )

  // Handle color field changes
  const handleColorChange = useCallback(
    (id) => (e) => {
      setOverrideData(createChangeEvent(id, e.target.value))
    },
    [createChangeEvent, setOverrideData],
  )

  const handleOtherStyleColorChange = useCallback(
    (id) => (e) => {
      setOverrideData((prev) => ({
        ...prev,
        otherStyles: {
          ...(prev?.otherStyles || {}),
          [id]: e.target.value,
        },
      }))
    },
    [setOverrideData],
  )

  const applyDrugAdviceColorDefaults = useCallback(() => {
    setOverrideData((prev) => ({
      ...prev,
      otherStyles: {
        ...(prev?.otherStyles || {}),
        ...DEFAULT_RX_PRINT_COLOR_STYLES,
      },
    }))
  }, [setOverrideData])

  // Handle slider changes
  const handleSliderChange = useCallback(
    (id) => (e, value) => {
      setOverrideData(createChangeEvent(id, value))
    },
    [createChangeEvent, setOverrideData],
  )

  // Handle adding new CSS property to otherStyles
  const handleAddOtherStyle = useCallback(() => {
    if (newStyleKey && newStyleValue) {
      setOverrideData((prev) => ({
        ...prev,
        otherStyles: {
          ...(prev?.otherStyles || {}),
          [newStyleKey]: newStyleValue,
        },
      }))
      setNewStyleKey('')
      setNewStyleValue('')
    }
  }, [setOverrideData, newStyleKey, newStyleValue])

  // Handle removing a CSS property from otherStyles
  const handleRemoveOtherStyle = useCallback(
    (key) => {
      setOverrideData((prev) => {
        const newOtherStyles = { ...(prev?.otherStyles || {}) }
        delete newOtherStyles[key]
        return {
          ...prev,
          otherStyles: newOtherStyles,
        }
      })
    },
    [setOverrideData],
  )
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <CustomSelect
          id="headingCase"
          label="Heading Case"
          value={overrideData?.headingCase || 'regular'}
          options={HEADING_CASE_OPTIONS}
          onChangeEvent={handleFieldChange}
          disabled={disabled}
          selectType={selectTypes.string}
          getOptionValue={(option) => option?.value}
          getOptionLabel={(option) => option?.label}
        />
      </Grid>
      {/* Heading Styles Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Heading Styles
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <CustomInput
          id="headingColor"
          label="Heading Color"
          type="color"
          value={overrideData?.headingColor || '#000000'}
          onChange={handleColorChange('headingColor')}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Heading Font Size (px)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Slider
              value={overrideData?.headingFontSize || 16}
              onChange={handleSliderChange('headingFontSize')}
              min={8}
              max={72}
              disabled={disabled}
              valueLabelDisplay="auto"
              sx={{ flex: 1 }}
            />
            <CustomInput
              id="headingFontSize"
              type="number"
              value={getNumberStringForInput(overrideData?.headingFontSize)}
              onChange={handleFieldChange}
              disabled={disabled}
              conponentContainerStyles={{ width: '80px' }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Body Styles Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Body Styles
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <CustomInput
          id="color"
          label="Text Color"
          type="color"
          value={overrideData?.color || '#000000'}
          onChange={handleColorChange('color')}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Font Size (px)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Slider
              value={overrideData?.fontSizePixels || 12}
              onChange={handleSliderChange('fontSizePixels')}
              min={6}
              max={48}
              disabled={disabled}
              valueLabelDisplay="auto"
              sx={{ flex: 1 }}
            />
            <CustomInput
              id="fontSizePixels"
              type="number"
              value={getNumberStringForInput(overrideData?.fontSizePixels)}
              onChange={handleFieldChange}
              disabled={disabled}
              conponentContainerStyles={{ width: '80px' }}
            />
          </Box>
        </Box>
      </Grid>

      {isDrugAdviceEmrSection(emrSectionId) && (
        <Grid item xs={12}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">Rx Table Colored Print Palette</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    onClick={applyDrugAdviceColorDefaults}
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                  >
                    Apply Professional Defaults
                  </Button>
                </Grid>
                {RX_PRINT_COLOR_STYLE_FIELDS.map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <CustomInput
                      id={field.key}
                      label={field.label}
                      type="color"
                      value={
                        getColorInputValue(
                          overrideData?.otherStyles?.[field.key],
                          DEFAULT_RX_PRINT_COLOR_STYLES[field.key],
                        )
                      }
                      onChange={handleOtherStyleColorChange(field.key)}
                      disabled={disabled}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {/* Advanced CSS Section - Collapsed by default */}
      <Grid item xs={12}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Advanced CSS Styles (Optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Display existing otherStyles */}
              {overrideData?.otherStyles &&
                Object.entries(overrideData.otherStyles).map(([key, value]) => (
                  <Grid item xs={12} key={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TextField
                        label="CSS Property"
                        value={key}
                        disabled
                        size="small"
                        style={{ flex: 1 }}
                      />
                      <TextField
                        label="Value"
                        value={value}
                        disabled
                        size="small"
                        style={{ flex: 1 }}
                      />
                      <IconButton
                        onClick={() => handleRemoveOtherStyle(key)}
                        disabled={disabled}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </Grid>
                ))}

              {/* Add new CSS property */}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  Add new CSS property (e.g., `&quot;font-weight: bold&quot;` or
                  `&quot;text-decoration: underline&quot;`)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TextField
                    label="CSS Property (e.g., font-weight)"
                    value={newStyleKey}
                    onChange={(e) => setNewStyleKey(e.target.value)}
                    size="small"
                    style={{ flex: 1 }}
                    disabled={disabled}
                  />
                  <TextField
                    label="Value (e.g., bold)"
                    value={newStyleValue}
                    onChange={(e) => setNewStyleValue(e.target.value)}
                    size="small"
                    style={{ flex: 1 }}
                    disabled={disabled}
                  />
                  <Button
                    onClick={handleAddOtherStyle}
                    disabled={!newStyleKey || !newStyleValue || disabled}
                    variant="outlined"
                    size="small"
                  >
                    Add
                  </Button>
                </div>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  )
}
